// src/pages/DeductionsPage.js
// บันทึกหักเงินพนักงาน (Admin Only)

import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { useNotification } from "../NotificationContext";

const DeductionsPage = () => {
  const { addNotification } = useNotification();
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  // Helper: Get local date string YYYY-MM-DD (not UTC)
  const getLocalDateString = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const today = new Date();
  const thisYear = today.getFullYear();
  const thisMonth = today.getMonth() + 1; // 1-12
  const firstDayIso = getLocalDateString(new Date(thisYear, thisMonth - 1, 1));
  const todayIso = getLocalDateString(today);
  const [filters, setFilters] = useState({ emp_id: "", date_from: firstDayIso, date_to: todayIso });
  const [filterMonth, setFilterMonth] = useState(thisMonth);
  const [filterYear, setFilterYear] = useState(thisYear);
  const [form, setForm] = useState({ emp_id: "", amount: "", deduct_date: "", note: "" });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);

  const loadEmployees = async () => {
    try {
      const res = await api.get("/employees");
      setEmployees(res.data || []);
    } catch { }
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const res = await api.get("/deductions", {
        params: {
          emp_id: filters.emp_id || undefined,
          // ส่งชื่อพารามิเตอร์ทั้งสองแบบเพื่อรองรับ backend สองเวอร์ชัน
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          start_date: filters.date_from || undefined,
          end_date: filters.date_to || undefined,
        },
      });
      let data = res.data || [];
      // client-side fallback ถ้า backend ไม่กรอง
      if (filters.emp_id) {
        data = data.filter(r => String(r.emp_id) === String(filters.emp_id));
      }
      if (filters.date_from) {
        try {
          const from = new Date(filters.date_from + 'T00:00:00');
          data = data.filter(r => {
            if (!r.deduct_date) return false;
            return new Date(r.deduct_date + 'T00:00:00') >= from;
          });
        } catch { }
      }
      if (filters.date_to) {
        try {
          const to = new Date(filters.date_to + 'T23:59:59');
          data = data.filter(r => {
            if (!r.deduct_date) return false;
            return new Date(r.deduct_date + 'T00:00:00') <= to;
          });
        } catch { }
      }
      setRows(data);
    } catch (e) {
      addNotification(e?.response?.data?.message || "โหลดข้อมูลไม่สำเร็จ", "danger");
    } finally { setLoading(false); }
  };

  // initial load (already has current month range)
  useEffect(() => { loadEmployees(); loadRows(); }, []);

  // when month/year selector changes, update date range and reload
  useEffect(() => {
    const start = getLocalDateString(new Date(filterYear, filterMonth - 1, 1));
    // If current month, use today as end date; otherwise use last day of month
    const isCurrentMonth = filterYear === thisYear && filterMonth === thisMonth;
    const end = isCurrentMonth ? getLocalDateString(new Date()) : getLocalDateString(new Date(filterYear, filterMonth, 0));
    setFilters(p => ({ ...p, date_from: start, date_to: end }));
  }, [filterMonth, filterYear]);

  // reload when filters.date_from/date_to changed by month/year adjustment
  useEffect(() => {
    loadRows();
  }, [filters.date_from, filters.date_to]);

  // reload when employee filter changes
  useEffect(() => {
    loadRows();
    setPage(1);
  }, [filters.emp_id]);

  // Utility: format money & date to match other pages
  const formatMoney = (val) => (Number(val) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('th-TH', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch { return iso; }
  };
  const totalCount = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageStart = (page - 1) * pageSize;
  const pageEnd = Math.min(totalCount, pageStart + pageSize);
  const pagedRows = rows.slice(pageStart, pageEnd);
  useEffect(() => { setPage(1); }, [rows, pageSize]);

  const resetForm = () => setForm({ emp_id: "", amount: "", deduct_date: "", note: "" });

  const handleCreate = async () => {
    if (!form.emp_id || !form.amount) {
      addNotification("กรุณาเลือกพนักงานและระบุยอดหัก", "warning");
      return;
    }
    try {
      await api.post("/deductions", { emp_id: form.emp_id, amount: Number(form.amount), deduct_date: form.deduct_date || undefined, note: form.note || undefined });
      addNotification("บันทึกเรียบร้อย", "success");
      resetForm();
      setIsCreateModalOpen(false);
      loadRows();
    } catch (e) {
      addNotification(e?.response?.data?.message || "บันทึกไม่สำเร็จ", "danger");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("ยืนยันลบรายการหักเงินนี้?")) return;
    try {
      await api.delete(`/deductions/${id}`);
      addNotification("ลบแล้ว", "success");
      loadRows();
    } catch (e) {
      addNotification(e?.response?.data?.message || "ลบไม่สำเร็จ", "danger");
    }
  };


  return (
    <>
      <div className="dashboard-page container">
        {/* Hero Header */}
        <div className="dashboard-hero mb-3">
          <div>
            <div className="dashboard-hero-title">
              <i className="bi bi-dash-circle me-2"></i>หักเงินพนักงาน
            </div>
            <div className="dashboard-hero-subtitle">บันทึกและจัดการรายการหักเงินตามช่วงวันที่</div>
          </div>
          <button className="btn btn-ks-primary" style={{ color: '#ffffff' }} onClick={() => setIsCreateModalOpen(true)}>
            <i className="bi bi-plus-circle me-1"></i>เพิ่มรายการหักเงิน
          </button>
        </div>

        <div className="card card-ks-soft">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0 fw-bold text-secondary"><i className="bi bi-list-ul me-2"></i>รายการหักเงิน (เดือนปัจจุบัน)</h5>
              <div className="text-muted small">ช่วงที่แสดง: {filters.date_from} ถึง {filters.date_to}</div>
            </div>



            {/* ตัวกรอง */}
            <div className="row g-2 mb-2">
              {/* แถวที่ 1: เลือกช่วงเวลา + พนักงาน */}
              <div className="col-md-3">
                <label className="form-label form-label-sm text-muted">เดือน</label>
                <select className="form-select form-select-sm" value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))}>
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map(m => (
                    <option key={m} value={m}>{['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'][parseInt(m) - 1]}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm text-muted">ปี</label>
                <select className="form-select form-select-sm" value={filterYear} onChange={e => setFilterYear(Number(e.target.value))}>
                  {Array.from({ length: 5 }, (_, i) => thisYear - i).map(y => (
                    <option key={y} value={y}>{y + 543}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-7">
                <label className="form-label form-label-sm text-muted">กรองตามพนักงาน</label>
                <select className="form-select form-select-sm" value={filters.emp_id} onChange={e => setFilters(p => ({ ...p, emp_id: e.target.value }))}>
                  <option value="">— ทั้งหมด —</option>
                  {employees.map(e => <option key={e.emp_id} value={e.emp_id}>{e.emp_name}</option>)}
                </select>
              </div>
            </div>

            <div className="row g-2 align-items-end mb-3">
              {/* แถวที่ 2: ช่วงวันที่แบบละเอียด + การตั้งค่า */}
              <div className="col-md-3">
                <label className="form-label form-label-sm text-muted">ตั้งแต่วันที่</label>
                <input type="date" className="form-control form-control-sm" value={filters.date_from} onChange={e => setFilters(p => ({ ...p, date_from: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label form-label-sm text-muted">ถึงวันที่</label>
                <input type="date" className="form-control form-control-sm" value={filters.date_to} onChange={e => setFilters(p => ({ ...p, date_to: e.target.value }))} />
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm text-muted">แถวต่อหน้า</label>
                <select className="form-select form-select-sm" value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm text-muted d-block">ยอดหักทั้งหมด</label>
                <div className="badge bg-danger" style={{ fontSize: '0.95rem', padding: '0.5rem 0.75rem' }}>
                  {formatMoney(rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0))} ฿
                </div>
              </div>
              <div className="col-md-2 d-grid">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => {
                  setFilterYear(thisYear);
                  setFilterMonth(thisMonth);
                }} title="รีเซ็ตเป็นเดือนนี้">
                  <i className="bi bi-arrow-clockwise me-1"></i>รีเซ็ต
                </button>
              </div>
            </div>


            {/* ตาราง */}
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light text-muted">
                  <tr>
                    <th style={{ width: 120 }}>วันที่</th>
                    <th>พนักงาน</th>
                    <th>หมายเหตุ</th>
                    <th className="text-end" style={{ width: 140 }}>ยอดหัก (บาท)</th>
                    <th style={{ width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={5} className="text-center text-muted py-4">ยังไม่มีข้อมูล</td></tr>
                  ) : pagedRows.map(r => (
                    <tr key={r.id}>
                      <td className="text-muted small">{formatDate(r.deduct_date)}</td>
                      <td className="fw-bold" style={{ color: "#d4af37" }}>{r.emp_name || r.emp_id}</td>
                      <td className="text-muted small">{r.note || '-'}</td>
                      <td className="text-end fw-bold text-danger">{formatMoney(r.amount)} ฿</td>
                      <td className="text-end">
                        <button className="btn btn-outline-danger btn-sm border-0" onClick={() => handleDelete(r.id)} title="ลบ">
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <div className="text-muted small">แสดง {totalCount === 0 ? 0 : pageStart + 1}-{pageEnd} จาก {totalCount} รายการ</div>
                <div className="btn-group">
                  <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>ก่อนหน้า</button>
                  <span className="btn btn-sm btn-light disabled">หน้า {page}/{totalPages}</span>
                  <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>ถัดไป</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
      {/* Modal เพิ่มรายการหักเงิน */}
      {
        isCreateModalOpen && (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title"><i className="bi bi-plus-circle me-2"></i>เพิ่มรายการหักเงิน</h5>
                  <button type="button" className="btn-close" onClick={() => { setIsCreateModalOpen(false); }}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label form-label-sm">พนักงาน<span className="text-danger"> *</span></label>
                    <select className="form-select form-select-sm" value={form.emp_id} onChange={e => setForm(p => ({ ...p, emp_id: e.target.value }))}>
                      <option value="">— เลือกพนักงาน —</option>
                      {employees.map(e => <option key={e.emp_id} value={e.emp_id}>{e.emp_name}</option>)}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label form-label-sm">ยอดหัก (บาท)<span className="text-danger"> *</span></label>
                    <input type="number" className="form-control form-control-sm" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label form-label-sm">วันที่</label>
                    <input type="date" className="form-control form-control-sm" value={form.deduct_date} onChange={e => setForm(p => ({ ...p, deduct_date: e.target.value }))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label form-label-sm">หมายเหตุ</label>
                    <input type="text" className="form-control form-control-sm" value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} placeholder="ระบุรายละเอียด (ไม่บังคับ)" />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setIsCreateModalOpen(false); resetForm(); }}>ยกเลิก</button>
                  <button type="button" className="btn btn-ks-success btn-sm" onClick={handleCreate}>
                    <i className="bi bi-save me-1"></i>บันทึก
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

export default DeductionsPage;
